import React, {useEffect, useMemo, useState} from 'react';
import {
  Button,
  Col,
  Descriptions,
  Flex,
  Form,
  InputNumber,
  message,
  Modal,
  Row,
  Space,
  Statistic,
  Typography
} from "antd";
import {API} from "../../http";
import {NEW_ENDPOINTS} from "../../http/endpoints";
import {ExclamationCircleFilled} from "@ant-design/icons";
import {GENERAL_SCHEME} from "../../schemas";
import {formatNumberPrecision} from "../../utils/helpers";

const CloseDayModal = ({open, onClose, onSubmit, ...props}) => {
  const [loading, setLoading] = useState(false);
  const [fees, setFees] = useState({
    fee: null,
    fee_tesla: null,
  });

  const [form] = Form.useForm();
  const balanceUsers = Form.useWatch('balance_users', form);
  const balanceUndefined = Form.useWatch('balance_undefined', form);
  const balanceOperational = Form.useWatch('balance_operational', form);
  const balanceTesla = Form.useWatch('balance_tesla', form);
  const balanceSafeguard = Form.useWatch('balance_safeguard', form);

  const fetchUserBalances = async () => {
    setLoading(true);
    let definedUserTotal = null;
    let undefinedUserTotal = null;

    try {
      const { data: definedUsers } = await API.post(NEW_ENDPOINTS.ADMIN.USER.GET_BALANCES, {
        user: '',
      });
      definedUserTotal = definedUsers.reduce((sum, current) => sum + Number(current.balance), 0);
    } catch (e) {
      console.error("Error fetching defined user balances:", e);
    }

    try {
      const { data: undefinedUsers } = await API.post(NEW_ENDPOINTS.ADMIN.USER.GET_BALANCES, {
        user: null,
      });
      undefinedUserTotal = undefinedUsers.reduce((sum, current) => sum + Math.abs(Number(current.balance)), 0);
    } catch (e) {
      console.error("Error fetching undefined user balances:", e);
    }

    setLoading(false);
    return { definedUserTotal, undefinedUserTotal };
  }
  const fetchFees = async () => {
    try {
      const {data} = await API.post(NEW_ENDPOINTS.ADMIN.ORDER.GET_FEE_BALANCES);
      setFees(data[0]);
      return data[0];
    } catch (e) {
      setFees({
        fee: null,
        fee_tesla: null,
      });
      return {
        fee: null,
        fee_tesla: null,
      };
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const { definedUserTotal, undefinedUserTotal } = await fetchUserBalances();
      const { fee, fee_tesla } = await fetchFees();
      if (definedUserTotal !== null && definedUserTotal !== undefined &&
        undefinedUserTotal !== null && undefinedUserTotal !== undefined &&
        fee !== null && fee !== undefined) {
        const teslaCalculated = definedUserTotal + +fee + undefinedUserTotal;
        form.setFieldsValue({
          balance_tesla: teslaCalculated,
          balance_users: definedUserTotal,
          balance_undefined: undefinedUserTotal,
        });
      }
    }

    if (open) fetchData();
  }, [open]);

  const onFinish = async (values) => {
    Modal.confirm({
      title: 'Day close confirmation',
      icon: <ExclamationCircleFilled/>,
      okButtonProps: {
        type: 'primary',
        size: 'large',
        danger: true,
      },
      cancelButtonProps: {
        size: 'large',
      },
      cancelText: "Edit values",
      okText: "Close day",
      width: 500,
      content: (
        <div>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Statistic title="Balance Users" value={values.balance_users} precision={4}/>
            </Col>
            <Col xs={24} sm={12}>
              <Statistic title="Balance Tesla" value={values.balance_tesla} precision={4}/>
            </Col>
            <Col xs={24} sm={12}>
              <Statistic title="Balance Safeguard" value={values.balance_safeguard} precision={4}/>
            </Col>
            <Col xs={24} sm={12}>
              <Statistic title="Balance Operational" value={values.balance_operational} precision={4}/>
            </Col>
          </Row>
        </div>
      ),
      centered: true,
      onOk: async () => {
        const { balance_undefined, ...formData } = values;
        try {
          setLoading(true);
          await API.post(NEW_ENDPOINTS.ADMIN.CLOSE_DAY.SUBMIT, formData);
          message.success('Day closed successfully');
          onSubmit?.(formData);
        } catch (error) {
          message.error('Failed to close the day');
        } finally {
          setLoading(false);
          onClose?.();
        }
      },
    });
  };
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      zIndex={800}
      destroyOnClose
      centered
      {...props}
    >
      <Descriptions column={2} title="Close Day Summary">
        <Descriptions.Item label="Defined Balances">{form.getFieldValue('balance_users') ?? '-/-'} EUR</Descriptions.Item>
        <Descriptions.Item label="Undefined Balances">{form.getFieldValue('balance_undefined') ?? '-/-'} EUR</Descriptions.Item>
        <Descriptions.Item label="Fee">
          {fees.fee} EUR
        </Descriptions.Item>
        <Descriptions.Item label="Fee Bank">
          {fees.fee_tesla} EUR
        </Descriptions.Item>
      </Descriptions>
      <Form preserve={false} disabled={loading} size="large" variant="filled" form={form} onFinish={onFinish} layout="vertical">
        <Row gutter={[16, 30]}>
          <Col xs={0}>
            <Form.Item
              name="balance_users"
              label="Users"
              rules={GENERAL_SCHEME.required}
              hidden
            >
              <InputNumber placeholder="0" min={0} step={0.0001} precision={4} style={{width: '100%'}}/>
            </Form.Item>
          </Col>
          <Col xs={0}>
            <Form.Item
              name="balance_undefined"
              label="Users"
              rules={GENERAL_SCHEME.required}
              hidden
            >
              <InputNumber placeholder="0" min={0} step={0.0001} precision={4} style={{width: '100%'}}/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="balance_tesla"
              label="Tesla (Calculated)"
              rules={GENERAL_SCHEME.required}
            >
              <InputNumber readOnly placeholder="0" min={0} step={0.0001} precision={4} style={{width: '100%'}}/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="balance_safeguard"
              label="iFast (SF)"
              rules={GENERAL_SCHEME.required}
            >
              <InputNumber placeholder="0" min={0} step={0.0001} precision={4} style={{width: '100%'}}/>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              name="balance_operational"
              label="iFast (OP)"
              rules={GENERAL_SCHEME.required}
            >
              <InputNumber placeholder="0" min={0} step={0.0001} precision={4} style={{width: '100%'}}/>
            </Form.Item>
          </Col>
        </Row>

        {(balanceSafeguard < balanceUsers + balanceUndefined) && (balanceSafeguard + balanceOperational) >= (balanceUsers + balanceUndefined) && (
          <Typography.Paragraph type="warning">
            Consider replenishing the safeguard account through the operational account ({ formatNumberPrecision((balanceUsers + balanceUndefined) - balanceSafeguard, 2, true)} EUR)
          </Typography.Paragraph>
        )}
        {(balanceSafeguard + balanceOperational) < (balanceUsers + balanceUndefined) && (
          <React.Fragment>
            <Typography.Paragraph type="danger">
              Transfer needed from Tesla to iFast ({formatNumberPrecision((balanceUsers + balanceUndefined) - (balanceSafeguard + balanceOperational), 2, true)} EUR)
            </Typography.Paragraph>
            {balanceTesla < balanceUsers + balanceUndefined - balanceSafeguard + balanceOperational && (
              <Typography.Paragraph type="danger">
                Insufficient funds in Tesla account to cover the shortfall
              </Typography.Paragraph>
            )}
          </React.Fragment>
        )}

        <Flex gap={10} className="w100" size="small">
          <Button disabled={balanceSafeguard + balanceOperational < balanceUsers + balanceUndefined} shape="round" type="primary" size="large" htmlType="submit" loading={loading} block>
            Continue
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default React.memo(CloseDayModal);
